// Centralised site content / company data
export const COMPANY = {
  legalName: "GTECGLOBAL COMPUTERS – FZCO",
  shortName: "GTECGLOBAL",
  tagline: "Global Technology. Reliable Solutions.",
  founded: "Dubai, UAE",
  address: {
    line1: "IFZA Business Park, DDP",
    line2: "PO Box 342001",
    city: "Dubai",
    country: "United Arab Emirates",
  },
  // Contact details
  phone: "+971553461506",
  whatsapp: "919952291125", // digits-only for wa.me link
  whatsappDisplay: "+91 99522 91125",
  email: "sales@gtecglobalcomp.com",
  salesEmail: "sales@gtecglobalcomp.com",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Products", to: "/products" },
  { label: "Services", to: "/services" },
  { label: "Brands", to: "/brands" },
  { label: "Contact", to: "/contact" },
];

export const VALUE_PROPS = [
  {
    code: "01",
    title: "Reliable global sourcing",
    description:
      "Authorised distribution channels and verified suppliers across Asia, Europe and the Americas.",
  },
  {
    code: "02",
    title: "Competitive wholesale pricing",
    description:
      "Direct manufacturer relationships and bulk procurement keep your unit economics healthy.",
  },
  {
    code: "03",
    title: "Quality technology products",
    description:
      "Genuine, traceable inventory with manufacturer warranty and rigorous QA on every order.",
  },
  {
    code: "04",
    title: "Fast quotation & response",
    description:
      "Detailed quotes within hours, not days. Live stock checks and clear lead times upfront.",
  },
  {
    code: "05",
    title: "International business support",
    description:
      "Export documentation, multi-currency invoicing and global logistics from our Dubai hub.",
  },
  {
    code: "06",
    title: "Customer-focused service",
    description:
      "A dedicated account manager from first quote to after-sales — built for long-term partnership.",
  },
];

export const PRODUCT_CATEGORIES = [
  {
    slug: "computers-laptops",
    title: "Computers & Laptops",
    summary:
      "Business desktops, workstations and laptops for enterprise, SMB and education rollouts.",
    items: [
      "Business laptops & ultrabooks",
      "Workstation desktops",
      "All-in-one PCs",
      "Mobile workstations",
    ],
    image:
      "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?auto=format&fit=crop&w=1200&q=70",
  },
  {
    slug: "components",
    title: "Computer Components",
    summary:
      "CPUs, memory, storage, GPUs, motherboards and power supplies from top-tier brands.",
    items: ["DRAM & memory modules", "SSD / HDD / NVMe", "CPUs & GPUs", "Motherboards"],
    image:
      "https://images.unsplash.com/photo-1636979648933-6d06b1ce9ad7?auto=format&fit=crop&w=1200&q=70",
  },
  {
    slug: "servers-storage",
    title: "Servers & Storage",
    summary:
      "Rack, tower and blade servers, SAN/NAS storage arrays and enterprise backup solutions.",
    items: ["Rack & tower servers", "SAN & NAS storage", "Tape & backup", "Server accessories"],
    image:
      "https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "networking",
    title: "Networking Equipment",
    summary:
      "Switches, routers, firewalls, Wi-Fi access points and structured cabling components.",
    items: ["Enterprise switches", "Routers & firewalls", "Wi-Fi 6 / 6E APs", "Optical transceivers"],
    image:
      "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&w=1200&q=70",
  },
  {
    slug: "semiconductors",
    title: "Semiconductors & Electronic Components",
    summary:
      "Integrated circuits, memory ICs, passives, connectors and discrete components for OEMs.",
    items: ["DRAM & NAND flash", "ICs & MCUs", "Passives & connectors", "Embedded modules"],
    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=70",
  },
  {
    slug: "accessories",
    title: "IT Accessories & Peripherals",
    summary:
      "Monitors, input devices, docking stations, cables, UPS units and everyday IT essentials.",
    items: ["Monitors & displays", "Keyboards & mice", "Docking stations", "UPS & power"],
    image:
      "https://images.unsplash.com/photo-1587202372775-e229f172b9d7?auto=format&fit=crop&w=1200&q=70",
  },
];

export const SERVICES = [
  {
    title: "IT Infrastructure Solutions",
    description:
      "End-to-end design, supply and commissioning of compute, storage, networking and data-centre infrastructure tailored to your workloads.",
    bullets: [
      "Data-centre supply & build-out",
      "Hybrid cloud hardware",
      "Network & security stack",
      "Lifecycle & refresh planning",
    ],
  },
  {
    title: "Bulk Procurement & International Trading",
    description:
      "High-volume sourcing of IT hardware and electronic components, with consolidated shipping from Dubai to global destinations.",
    bullets: [
      "Project-based bulk orders",
      "Multi-brand consolidation",
      "Export documentation",
      "Multi-currency invoicing",
    ],
  },
  {
    title: "Component & Semiconductor Sourcing",
    description:
      "Hard-to-find ICs, memory, and electronic components sourced through verified distribution and franchise channels.",
    bullets: [
      "Obsolete & allocated parts",
      "Authorised channel verified",
      "Date-code & lot traceability",
      "Quality inspection on request",
    ],
  },
  {
    title: "Corporate IT Supply",
    description:
      "Standardised hardware rollouts for offices, branches and remote teams — quoted, kitted and shipped from one partner.",
    bullets: [
      "Multi-site rollouts",
      "Standard SKUs & imaging",
      "Warranty management",
      "Dedicated account manager",
    ],
  },
];

export const INDUSTRIES = [
  "Government & Public Sector",
  "Banking & Financial Services",
  "Telecom & ISPs",
  "Oil, Gas & Energy",
  "Healthcare & Pharma",
  "Education & Research",
  "Retail & Hospitality",
  "Logistics & Trading",
];

export const BRANDS = [
  { name: "INTEL", slug: "intel" },
  { name: "AMD", slug: "amd" },
  { name: "SAMSUNG", slug: "samsung" },
  { name: "MICRON", slug: "micron", logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-138/artifacts/1hlmw2hw_micron-logo-1024x1024.png" },
  { name: "SK HYNIX", slug: "skhynix", logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-138/artifacts/1srmz36r_sk-hynix-logo-png-transparent.png" },
  { name: "DELL", slug: "dell" },
  { name: "HP", slug: "hp" },
  { name: "LENOVO", slug: "lenovo" },
  { name: "CISCO", slug: "cisco" },
  { name: "ASUS", slug: "asus" },
  { name: "GIGABYTE", slug: "gigabytetechnology", logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-138/artifacts/jm6rchop_png-transparent-logo-gigabyte-technology-graphics-font-a2s-logo-blue-angle-text.png" },
  { name: "MSI", slug: "msibusiness" },
  { name: "WESTERN DIGITAL", slug: "westerndigital", logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-138/artifacts/xchsfrre_purepng.com-western-digital-logologobrand-logoiconslogos-251519940757if6ch.png" },
  { name: "SEAGATE", slug: "seagate" },
  { name: "KINGSTON", slug: "kingstontechnology" },
  { name: "CORSAIR", slug: "corsair" },
  { name: "TP-LINK", slug: "tplink" },
  { name: "ARUBA", slug: "arubanetworks", logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-138/artifacts/bi9kz4wf_aruba-networks-logo-png_seeklogo-252157.png" },
  { name: "FORTINET", slug: "fortinet" },
  { name: "JUNIPER", slug: "junipernetworks" },
  { name: "HUAWEI", slug: "huawei" },
  { name: "APC", slug: "schneiderelectric" },
  { name: "EATON", slug: "eaton", logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-138/artifacts/wgwpcymx_Eaton-Logo.png" },
  { name: "LOGITECH", slug: "logitech", logo: "https://customer-assets.emergentagent.com/job_tech-solutions-hub-138/artifacts/avgfduyy_Logitech-Emblema.png" },
];

export const STATS = [
  { value: "150+", label: "Brands sourced" },
  { value: "40+", label: "Countries shipped" },
  { value: "24h", label: "Quote turnaround" },
  { value: "10K+", label: "SKUs available" },
];
